import { createContext, useContext, useEffect, useState } from 'react';

import type { Crumb } from '../components/page-breadcrumbs';

/** A nested page's own trailing breadcrumb — everything after "Runs › run title". Replaces the
 *  shell's own single active-tab crumb entirely, so the page controls both the (clickable) tab
 *  crumb it's reached from and its own leaf (e.g. a product's name + thumbnail, or "Compare"). */
export interface RunBreadcrumbTail {
  crumbs: Crumb[];
}

/** `undefined` outside a `RunShell` (or before it has mounted its setter) — a page calling the
 *  hook below then simply has no effect, rather than needing a real no-op default. Kept in its
 *  own module (not `run-shell.tsx`) so this hook's export doesn't disable that component's Fast
 *  Refresh. */
export const RunBreadcrumbTailCtx = createContext<
  ((tail: RunBreadcrumbTail | undefined) => void) | undefined
>(undefined);

/**
 * R1-1 — lets a page nested under `RunShell` (compare, content results, product-in-batch) supply
 * its own trailing breadcrumb instead of rendering a second, fuller `PageBreadcrumbs` of its own:
 * the shell is now the ONE place the trail (and the "← Back to <origin>" chip) renders. Pass
 * `undefined` for a page that's happy with the shell's own default (single active-tab) crumb.
 * `key` (optional) names what the tail shows; when given, the shell is only updated when the key
 * changes, so a page that rebuilds its crumbs on every render (e.g. on a progress poll) doesn't
 * re-render the shell each time. Without a key, `crumbs` itself should be referentially stable.
 */
export function useRunBreadcrumbTail(crumbs: Crumb[] | undefined, key?: string): void {
  const setTail = useContext(RunBreadcrumbTailCtx);
  // Keyed: hold the crumbs captured when the key last changed (React's "adjust state when a value
  // changes" pattern), so a fresh array with the same key is ignored.
  const keyed = key ?? null;
  const [snapshot, setSnapshot] = useState({ key: keyed, crumbs });
  if (keyed !== null && snapshot.key !== keyed) setSnapshot({ key: keyed, crumbs });
  const effective = keyed === null ? crumbs : snapshot.crumbs;
  useEffect(() => {
    setTail?.(effective ? { crumbs: effective } : undefined);
    return () => {
      setTail?.(undefined);
    };
  }, [effective, setTail]);
}
